import {
  Avatar,
  AvatarFallback,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarTrigger,
} from '@osteopro/ui';
import { LogOut, Search, Settings as SettingsIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { useAuth, usePlatformOnly } from '../../lib/auth-provider.js';
import { initials } from '../../lib/format.js';
import { HelpButton } from '../help/HelpButton.js';
import { GlobalSearch } from './GlobalSearch.js';
import { LanguageToggle } from './LanguageToggle.js';
import { ThemeToggle } from './ThemeToggle.js';

/** En-tête collant de la coquille : recherche, aide de l'écran, langue, thème, compte. */
export function Header() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  // La recherche porte sur les dossiers du cabinet : un compte de plateforme sans cabinet n'en a pas.
  const platformOnly = usePlatformOnly();
  const [, navigate] = useLocation();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const displayName = user ? `${user.firstName} ${user.lastName}`.trim() : '';

  return (
    <header className="sticky top-0 z-50 flex h-16 items-center gap-4 border-b bg-background/85 px-4 backdrop-blur-md md:px-6">
      <SidebarTrigger className="md:hidden" data-testid="button-sidebar-toggle" />

      <div className="flex flex-1 items-center gap-4">
        {!platformOnly && (
          <>
            <GlobalSearch className="hidden max-w-md flex-1 md:block" shortcut />

            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => {
                setMobileSearchOpen(true);
              }}
              data-testid="button-mobile-search"
            >
              <Search className="h-5 w-5" />
            </Button>
          </>
        )}

        <Dialog open={mobileSearchOpen} onOpenChange={setMobileSearchOpen}>
          <DialogContent className="top-4 translate-y-0" data-testid="dialog-mobile-search">
            <DialogHeader>
              <DialogTitle>{t('shell:search.title')}</DialogTitle>
            </DialogHeader>
            <GlobalSearch
              className="w-full"
              onNavigate={() => {
                setMobileSearchOpen(false);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2">
        <HelpButton />
        <LanguageToggle />
        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="relative h-9 w-9 rounded-full"
              data-testid="button-profile"
            >
              <Avatar className="h-9 w-9">
                <AvatarFallback>{initials(user ?? {})}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{displayName}</p>
                <p className="text-xs leading-none text-muted-foreground">{user?.email ?? ''}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                navigate('/settings');
              }}
              data-testid="menu-settings"
            >
              <SettingsIcon className="mr-2 h-4 w-4" />
              {t('settings:title')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => void logout()}
              data-testid="menu-logout"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t('shell:account.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
